import React, { useState, useEffect } from 'react';
import { apiFetch, getApiHost } from '../utils/api';

interface StreamDetail {
  name: string;
  lockedAmount: string;
  percentOfSupply: number;
  lockedIn48h: string;
  percentLockedIn48h: number;
  unlockDate: string;
  recipient: string;
  escrowAddress: string;
  status: 'locked' | 'vesting' | 'fully_unlocked';
}

interface StreamflowData {
  mint: string;
  totalLocked: string;
  totalLockedPercent: number;
  activeStreamCount: number;
  totalLockedIn48h: string;
  totalLockedIn48hPercent: number;
  topStreams: StreamDetail[];
  decimals: number;
}

interface Props {
  mint: string;
}

function formatTokenAmount(raw: string, decimals: number): string {
  const num = Number(raw) / Math.pow(10, decimals);
  if (num >= 1_000_000_000) return `${(num / 1_000_000_000).toFixed(2)}B`;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(2)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(2)}K`;
  return num.toFixed(2);
}

function formatDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

const StreamflowLocks: React.FC<Props> = ({ mint }) => {
  const [data, setData] = useState<StreamflowData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchLocks = async () => {
      try {
        setLoading(true);
        const res = await apiFetch(`${getApiHost()}/api/streamflow-locks/${mint}`);
        if (!res.ok) throw new Error();
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchLocks();
    return () => { cancelled = true; };
  }, [mint]);

  // Don't render anything if loading, error, or nothing actually locked
  if (loading || error || !data || (data.activeStreamCount === 0 && data.totalLocked === '0') || data.totalLockedPercent === 0) return null;

  const { totalLockedPercent, activeStreamCount, totalLockedIn48hPercent, topStreams, decimals } = data;

  return (
    <div className="mb-6">
      <h3 className="text-xl font-bold mb-4 flex items-center">
        <i className="fas fa-lock text-blue-400 mr-3"></i>
        Locked Supply
        <a
          href={`https://app.streamflow.finance/token-dashboard/solana/mainnet/${mint}`}
          target="_blank"
          rel="noopener noreferrer"
          className="ml-2 text-sm font-normal text-blue-400 hover:text-blue-300 transition-colors"
        >
          via Streamflow <i className="fas fa-external-link-alt text-xs ml-1"></i>
        </a>
      </h3>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <div className="bg-crypto-card border border-gray-700 rounded-lg p-4">
          <div className="text-sm text-gray-400 mb-1">Tokens Locked</div>
          <div className="text-white font-bold text-lg">
            {formatTokenAmount(data.totalLocked, decimals)}
          </div>
        </div>
        <div className="bg-crypto-card border border-gray-700 rounded-lg p-4">
          <div className="text-sm text-gray-400 mb-1">% of Total Supply</div>
          <div className="text-blue-400 font-bold text-lg">
            {totalLockedPercent.toFixed(2)}%
          </div>
        </div>
        <div className="bg-crypto-card border border-gray-700 rounded-lg p-4">
          <div className="text-sm text-gray-400 mb-1">Active Lock Streams</div>
          <div className="text-white font-bold text-lg">
            {activeStreamCount}
          </div>
        </div>
        <div className="bg-crypto-card border border-gray-700 rounded-lg p-4">
          <div className="text-sm text-gray-400 mb-1">Still Locked in 48h</div>
          <div className="text-green-400 font-bold text-lg">
            {totalLockedIn48hPercent.toFixed(2)}%
          </div>
        </div>
      </div>

      {/* Top Streams Table */}
      {topStreams.length > 0 && (
        <div className="bg-crypto-card border border-gray-700 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-crypto-dark border-b border-gray-700">
                <tr>
                  <th className="text-left p-3 text-sm font-medium text-gray-400">Stream</th>
                  <th className="text-right p-3 text-sm font-medium text-gray-400">Locked Amount</th>
                  <th className="text-right p-3 text-sm font-medium text-gray-400">% Supply</th>
                  <th className="text-right p-3 text-sm font-medium text-gray-400 hidden md:table-cell">Unlocks</th>
                  <th className="text-right p-3 text-sm font-medium text-gray-400 hidden sm:table-cell">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {topStreams.map((stream, idx) => (
                  <tr key={idx} className="hover:bg-crypto-dark/50 transition-colors">
                    <td className="p-3">
                      <span className="text-white text-sm font-medium">
                        {stream.name && stream.name.replace(/\0/g, '').trim()
                          ? stream.name.replace(/\0/g, '').trim()
                          : stream.recipient
                            ? <a
                                href={`/wallet/${stream.recipient}`}
                                className="text-blue-400 hover:text-blue-300 font-mono transition-colors"
                                title={stream.recipient}
                              >
                                {stream.recipient.slice(0, 4)}...{stream.recipient.slice(-4)}
                              </a>
                            : `Stream ${idx + 1}`
                        }
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      <span className="text-white text-sm">
                        {formatTokenAmount(stream.lockedAmount, decimals)}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      <span className="text-blue-400 text-sm font-medium">
                        {stream.percentOfSupply.toFixed(2)}%
                      </span>
                    </td>
                    <td className="p-3 text-right hidden md:table-cell">
                      <span className="text-gray-300 text-sm">
                        {formatDate(stream.unlockDate)}
                      </span>
                    </td>
                    <td className="p-3 text-right hidden sm:table-cell">
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                        stream.status === 'locked'
                          ? 'bg-blue-500/20 text-blue-400'
                          : stream.status === 'vesting'
                          ? 'bg-yellow-500/20 text-yellow-400'
                          : 'bg-gray-500/20 text-gray-400'
                      }`}>
                        {stream.status === 'locked' ? 'Locked' : stream.status === 'vesting' ? 'Vesting' : 'Unlocked'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {activeStreamCount > topStreams.length && (
            <div className="p-3 border-t border-gray-700 text-center text-sm text-gray-500">
              Showing top {topStreams.length} of {activeStreamCount} active lock streams
            </div>
          )}
          <div className="p-3 border-t border-gray-700 text-center text-xs text-gray-600">
            Lock data sourced from Streamflow Finance
          </div>
        </div>
      )}
    </div>
  );
};

export default StreamflowLocks;
